import axios from 'axios';

// Usar variável de ambiente ou fallback para localhost:3000
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    // Desabilitar cache nas requisições
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0'
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add any auth tokens or other headers here
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors here
    if (error.response?.status === 401) {
      // Handle unauthorized
      console.error('Unauthorized access');
    } else if (error.response?.status === 500) {
      // Handle server errors
      console.error('Server error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Generic CRUD service factory
const createCrudService = (endpoint) => ({
  getAll: () => api.get(`/${endpoint}`),
  getById: (id) => api.get(`/${endpoint}/${id}`),
  create: (data) => api.post(`/${endpoint}`, data),
  update: (id, data) => api.patch(`/${endpoint}/${id}`, data),
  delete: (id) => api.delete(`/${endpoint}/${id}`),
});

// Export services for each entity
export const caravanaService = createCrudService('caravana');
export const igrejaService = createCrudService('igreja');
export const campoService = createCrudService('campo');
export const eventoService = {
  ...createCrudService('evento'),
  
  // Buscar dashboard do evento
  getDashboard: (id) => api.get(`/evento/${id}/dashboard`),
};
export const responsavelCaravanaService = createCrudService('responsavel_caravana');
export const quartoService = createCrudService('quarto');
export const localService = createCrudService('local');
export const caixaService = {
  ...createCrudService('caixa'),
  getAbertos: async () => {
    try {
      const response = await api.get('/caixa/abertos');
      // Backend retorna {success: true, data: [...]}
      const caixasData = response.data?.data || [];
      return {
        ...response,
        data: Array.isArray(caixasData) ? caixasData : []
      };
    } catch (error) {
      console.error('Erro ao buscar caixas abertos:', error);
      return { data: [] };
    }
  },
};
export const planoContaService = createCrudService('plano_conta');
export const movimentacaoCaixaService = {
  ...createCrudService('movimentacao_caixa'),
  
  // Criar com anexo
  createWithFile: (data, file) => {
    const formData = new FormData();
    
    // Adicionar dados do formulário
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    // Adicionar arquivo se existir
    if (file) {
      formData.append('anexo', file);
    }
    
    return api.post('/movimentacao_caixa', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Atualizar com anexo
  updateWithFile: (id, data, file) => {
    const formData = new FormData();
    
    // Adicionar dados do formulário
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
    
    // Adicionar arquivo se existir
    if (file) {
      formData.append('anexo', file);
    }
    
    return api.patch(`/movimentacao_caixa/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Buscar anexo
  getAnexo: (id) => api.get(`/movimentacao_caixa/${id}/anexo`, {
    responseType: 'blob', // Para receber dados binários
  }),
};
export const ocorrenciaTipoService = createCrudService('ocorrencia_tipo');
export const ocorrenciaService = {
  ...createCrudService('ocorrencia'),
  getKanban: (eventoId) => api.get(`/ocorrencia/evento/${eventoId}`),
  updateStatus: (id, data) => api.patch(`/ocorrencia/${id}/status`, data),
};

export const alocacaoService = {
  ...createCrudService('alocacao'),
  
  // Buscar alocações por evento
  getByEvento: (eventoId) => api.get(`/alocacao?evento_id=${eventoId}`),
  
  // Buscar alocações por quarto
  getByQuarto: (quartoId, eventoId) => {
    const params = new URLSearchParams({ quarto_id: quartoId });
    if (eventoId) params.append('evento_id', eventoId);
    return api.get(`/alocacao?${params.toString()}`);
  },
  
  // Buscar alocações por caravana
  getByCaravana: (caravanaId, eventoId) => {
    const params = new URLSearchParams({ caravana_id: caravanaId });
    if (eventoId) params.append('evento_id', eventoId);
    return api.get(`/alocacao?${params.toString()}`);
  },
  
  // Buscar estatísticas de alocação por evento
  getEstatisticas: (eventoId) => api.get(`/alocacao/estatisticas/${eventoId}`),
};

// Parcela Caravana service
export const parcelaCaravanaService = {
  ...createCrudService('parcelas_caravana'),
  
  // Buscar parcelas por caravana
  getByCaravana: (caravanaId) => api.get(`/parcelas_caravana?caravana_id=${caravanaId}`),
  
  // Buscar parcelas por status
  getByStatus: (status) => api.get(`/parcelas_caravana?status=${status}`),
  
  // Buscar parcelas por caravana e status
  getByCaravanaAndStatus: (caravanaId, status) => 
    api.get(`/parcelas_caravana?caravana_id=${caravanaId}&status=${status}`),
};

// Dashboard service
export const dashboardService = {
  getDashboardData: () => api.get('/dashboard'),
};

export default api;